import {
	SET_CONFIGURATIONS,
	SET_HAS_CONFIGURATION_CHANGES,
	UPDATE_CONFIGURATION,
	CLEAR_UPDATED_CONFIGURATIONS,
	SET_CONFIGURATIONS_AFTER_UPDATE,
	SET_CONFIG_NAMES,
	SET_CATEGORIES_SUBCATEGORIES,
	SET_CHANGED_BY_NAMES,
	SET_HISTORY,
	RESET_HISTORY
} from './configuration-mutation-types';
import api from '@/services/api-service';
import buildRoute from '@/composables/build-route';
import { cloneDeep } from 'lodash';
import Vue from 'vue';
import { findConfig } from '@/store/modules/configurations/configuration-utils';
import { objectHasProperty } from '@/utils/object-functions';

export default {
	strict: process.env.NODE_ENV !== 'production',
	name: 'configuration',
	namespaced: true,
	state: {
		configurations: {},
		updatedConfigurations: [],
		hasConfigChanges: false,
		updateConfigActionName: 'Save',
		removeConfigActionName: 'Delete',
		configNames: {
			items: []
		},
		categoriesSubcategories: {
			items: []
		},
		changedByNames: {
			items: []
		},
		history: {}
	},
	mutations: {
		[SET_HISTORY](state, history) {
			state.history = history;
		},
		[SET_CONFIG_NAMES](state, configNames) {
			state.configNames = configNames;
		},
		[SET_CATEGORIES_SUBCATEGORIES](state, categoriesSubcategories) {
			state.categoriesSubcategories = categoriesSubcategories;
		},
		[SET_CHANGED_BY_NAMES](state, changedByNames) {
			state.changedByNames = changedByNames;
		},
		[SET_CONFIGURATIONS](state, configurations) {
			state.configurations = configurations;
		},
		[SET_CONFIGURATIONS_AFTER_UPDATE](
			state,
			{ configurations, updatedConfigurations }
		) {
			let deletedConfigurations;
			let foundConfig;

			state.configurations.lastUpdated = configurations.lastUpdated;

			if (configurations && configurations.items) {
				configurations.items.forEach(configuration => {
					foundConfig = findConfig(
						configuration,
						state.configurations.items
					);

					if (foundConfig.index < 0) {
						//a new config
						state.configurations.items.push(configuration);
					} else {
						state.configurations.items.splice(
							foundConfig.index,
							1,
							configuration
						);
					}
				});
			}

			if (updatedConfigurations) {
				deletedConfigurations = updatedConfigurations.filter(c => {
					return c.action === state.removeConfigActionName;
				});

				if (deletedConfigurations) {
					deletedConfigurations.forEach(configuration => {
						foundConfig = findConfig(
							configuration,
							state.configurations.items
						);

						if (foundConfig.index >= 0) {
							state.configurations.items.splice(
								foundConfig.index,
								1
							);
						}
					});
				}
			}
		},
		[SET_HAS_CONFIGURATION_CHANGES](state, hasConfigChanges) {
			state.hasConfigChanges = hasConfigChanges;
		},
		[CLEAR_UPDATED_CONFIGURATIONS](state) {
			Vue.set(state, 'updatedConfigurations', []);
		},
		[UPDATE_CONFIGURATION](
			state,
			{ configuration, action, postSaveAction }
		) {
			let foundConfig;

			configuration.action = action;
			configuration.postSaveAction = postSaveAction;

			foundConfig = findConfig(
				configuration,
				state.updatedConfigurations
			);

			if (foundConfig.index >= 0) {
				state.updatedConfigurations.splice(
					foundConfig.index,
					1,
					configuration
				);
			} else {
				state.updatedConfigurations.push(configuration);
			}
		},
		[RESET_HISTORY](state) {
			state.history = {};
		}
	},
	actions: {
		// actions let us get to ({ state, getters, commit, dispatch }) {
		async getHistoryAction(
			{ commit },
			{
				companyId,
				logType,
				minDatetime,
				maxDatetime,
				module,
				category,
				subcategory,
				displayName,
				changedByUserId,
				orderBy,
				orderDirection,
				recordStart,
				maxCount
			}
		) {
			const history = await api.get(
				buildRoute('/logs/configurations/history', {
					query: {
						companyId,
						logType,
						minDatetime,
						maxDatetime,
						module,
						category,
						subcategory,
						displayName,
						changedByUserId,
						orderBy,
						orderDirection,
						recordStart,
						maxCount
					}
				})
			);
			commit(SET_HISTORY, history);
		},
		async getConfigNamesAction(
			{ commit },
			{ companyId, module }
		) {
			const configNames = await api.get(
				buildRoute('/logs/configurations/confignames', {
					query: {
						companyId,
						module
					}
				})
			);
			commit(SET_CONFIG_NAMES, configNames);
		},
		async getCategoriesSubcategoriesAction(
			{ commit },
			{ companyId, module }
		) {
			const categoriesSubcategories = await api.get(
				buildRoute('/logs/configurations/categoriessubcategories', {
					query: {
						companyId,
						module
					}
				})
			);
			commit(SET_CATEGORIES_SUBCATEGORIES, categoriesSubcategories);
		},
		async getChangedByNamesAction(
			{ commit },
			{ companyId, module }
		) {
			const changedByNames = await api.get(
				buildRoute('/logs/configurations/changedbyusers', {
					query: {
						companyId,
						module
					}
				})
			);
			commit(SET_CHANGED_BY_NAMES, changedByNames);
		},
		async getConfigurationsAction(
			{ commit },
			{ companyId, locationId, configurationKeyIds }
		) {
			const configurations = await api.get(
				buildRoute('/dealership/company/{companyId}/configurations', {
					companyId: companyId,
					query: {
						locationId: locationId,
						id: configurationKeyIds
					}
				})
			);
			commit(SET_CONFIGURATIONS, configurations);
		},
		setHasConfigChangesAction({ commit }, hasConfigChanges) {
			commit(SET_HAS_CONFIGURATION_CHANGES, hasConfigChanges);
		},
		updateConfigAction({ dispatch, state }, { config, postSaveAction }) {
			const action =
				config?.keyValue === undefined
					? state.removeConfigActionName
					: state.updateConfigActionName;

			dispatch('changeUpdatedConfigsAction', {
				config,
				action,
				postSaveAction
			});
		},
		removeConfigAction({ dispatch, state }, { config, postSaveAction }) {
			const action = state.removeConfigActionName;

			dispatch('changeUpdatedConfigsAction', {
				config,
				action,
				postSaveAction
			});
		},
		changeUpdatedConfigsAction(
			{ commit },
			{ config, action, postSaveAction }
		) {
			commit(UPDATE_CONFIGURATION, {
				configuration: config,
				action,
				postSaveAction
			});
		},
		async saveConfigurationsAction(
			{ dispatch, commit, getters },
			companyId
		) {
			//TODO: process getters.updatedConfigurations to remove items that are removed but don't have an id
			const configurations = await api.post(
				buildRoute('/dealership/company/{companyId}/configurations', {
					companyId: companyId
				}),
				getters.updatedConfigurations
			);

			commit(SET_CONFIGURATIONS_AFTER_UPDATE, {
				configurations,
				updatedConfigurations: getters.updatedConfigurations
			});

			await dispatch(
				'executePostSaveAction',
				getters.updatedConfigurations
			);

			commit(CLEAR_UPDATED_CONFIGURATIONS);
		},
		// eslint-disable-next-line no-unused-vars
		async executePostSaveAction({ commit }, configurations) {
			if (configurations) {
				configurations.forEach(async config => {
					if (config.postSaveAction) {
						await config.postSaveAction(config);
					}
				});
			}
		},
		async resetToDefaultAction({ getters, dispatch }, configurationKeyIds) {
			let config;

			configurationKeyIds.forEach(async configurationKeyId => {
				config = cloneDeep(
					getters.configsByKeyId(configurationKeyId)[0]
				);

				config.keyValueCustom = config.keyValue;
				config.keyValue = config.defaultValue;

				await dispatch('updateConfigAction', {
					config: config
				});
			});
		},
		async resetToCustomAction({ getters, dispatch }, configurationKeyIds) {
			let config;

			configurationKeyIds.forEach(async configurationKeyId => {
				config = cloneDeep(
					getters.configsByKeyId(configurationKeyId)[0]
				);

				if (objectHasProperty(config, 'keyValueCustom')) {
					config.keyValue = config.keyValueCustom;

					await dispatch('updateConfigAction', {
						config: config
					});
				}
			});
		},
		resetHistory({ commit }) {
			commit(RESET_HISTORY);
		}
	},
	getters: {
		history: state => state.history,
		configNames: state => state.configNames,
		categoriesSubcategories: state => state.categoriesSubcategories,
		changedByNames: state => state.changedByNames,
		configurations: state => state.configurations,
		hasConfigChanges: state => state.hasConfigChanges,
		configsByKeyId: state => configKeyId => {
			let configs;
			if (state.configurations?.items) {
				let updated = state.updatedConfigurations.filter(
					c => c.configurationKeyId === configKeyId
				);
				configs = updated.length
					? updated
					: state.configurations.items.filter(
							c => c.configurationKeyId === configKeyId
					  );
			}

			return configs;
		},
		entityConfig: state => ({
			companyId,
			entityId,
			entityTypeId,
			configurationKeyId
		}) => {
			let config, updatedConfigurations, updatedkeyValue;

			if (state.configurations?.items) {
				config = state.configurations.items.find(
					c =>
						c.companyId === companyId &&
						(c.entityId === entityId || c.id === 0) &&
						c.entityTypeId === entityTypeId &&
						c.configurationKeyId === configurationKeyId
				);
				updatedConfigurations = state.updatedConfigurations.find(
					c =>
						c.companyId === companyId &&
						c.entityId === entityId &&
						c.entityTypeId === entityTypeId &&
						c.configurationKeyId === configurationKeyId
				);

				updatedkeyValue = updatedConfigurations?.keyValue;
			}

			if (!config) {
				//if the config doesn't exist yet, then this default obj will be used to create a new one on save
				config = {
					companyId: companyId,
					entityId: entityId,
					entityTypeId: entityTypeId,
					configurationKeyId: configurationKeyId,
					keyValue: undefined
				};
			}

			config = cloneDeep(config);
			config.keyValue =
				updatedkeyValue === undefined
					? config?.keyValue
					: updatedkeyValue;
			return config;
		},
		updatedConfigurations: state => state.updatedConfigurations,
		updateConfigActionName: state => state.updateConfigActionName,
		removeConfigActionName: state => state.removeConfigActionName,
		lastUpdated: state => state.configurations?.lastUpdated
	}
};
